import { getLocationGeneralConfig, useGeneralConfig } from '~/core/dapi/generalConfig';
import history from '~/core/history';
import { ldClient } from '~/core/launch-darkly/client';
import { useLDFlags } from '~/core/launch-darkly/hooks/useLDFlags';
import { RemixRoutes } from '~/core/remix';

type V1AddCardPageVariant = 'add-card/r' | 'add-card-bf' | 'add-card-pre-visit';

/**
 * Whether, for a given location, both Solv Pay V2 AND the feature flag enabling pre-visit
 * payment methods are enabled.
 * @param locationId The location ID to check
 * @returns Whether both Solv Pay V2 and the feature flag are enabled
 */
async function isPreVisitV2SolvPayEnabled(locationId: string) {
  const useSolvPayV2 = await getLocationGeneralConfig<boolean>('use_solv_pay_v2', locationId, true);

  const enablePreVisitV2 = ldClient?.variation(
    'pay-66-enable-pre-visit-new-payment-methods',
    false
  ) as boolean;

  return useSolvPayV2 && enablePreVisitV2;
}

/**
 * Based on whether Solv Pay V2 is enabled, dynamically redirects the user to the right
 * "add payment method" page.
 *
 * @param bookingId The booking ID the payment method is for
 * @param useSolvPayV2 Whether the location for the booking is using Solv Pay V2
 */
export async function redirectToAddPaymentMethodPage({
  bookingId,
  locationId,
  paymentVariant,
  returnUrl,
  turboRedirectOrigin,
}: {
  bookingId: string;
  locationId: string;
  paymentVariant: V1AddCardPageVariant;
  returnUrl: string;
  turboRedirectOrigin?: Parameters<typeof RemixRoutes.payments.addCard>[2];
}) {
  if (await isPreVisitV2SolvPayEnabled(locationId)) {
    window.location.href = RemixRoutes.payments.addCard(bookingId, returnUrl, turboRedirectOrigin);
    return;
  }

  // If we accidentally already have a leading slash,
  // it will crash some browsers with a "security error" sometimes
  history.push(`/payments/${paymentVariant}/${returnUrl.replace(/^\//, '')}`);
}

/**
 * Based on whether Solv Pay V2 is enabled, dynamically redirects the user to the right
 * "add payment method" page.
 *
 * @param bookingId The booking ID the payment method is for
 * @param useSolvPayV2 Whether the location for the booking is using Solv Pay V2
 */
export async function redirectToAddPaymentMethodPageFromPassPage(
  bookingId: string,
  locationId: string
) {
  redirectToAddPaymentMethodPage({
    bookingId,
    locationId,
    paymentVariant: 'add-card/r',
    returnUrl: `/pass/${bookingId}`,
    turboRedirectOrigin: 'mapp',
  });
}

/**
 * Based on whether Solv Pay V2 is enabled, dynamically redirects the user to the right
 * "add payment method" page for ~during the booking flow~ before we have a booking ID.
 * @param locationId The location ID the payment method is for
 * @param nextPage The "page" in the booking flow that is up next. This isn't a URL but a value that
 *                 the booking flow code knows how to handle.
 */
export async function redirectToAddPaymentMethodDuringNewBookingPage(
  locationId?: string,
  nextPage?: string
) {
  if (!locationId) {
    console.error('Attempted to redirect to add payment method page without a location ID');
    return;
  }

  if (await isPreVisitV2SolvPayEnabled(locationId)) {
    window.location.href = RemixRoutes.payments.addCardDuringNewBooking(locationId, nextPage);
  } else {
    history.push(`/payments/add-card-bf/${nextPage}`);
  }
}

export function useIsSolvPayV2Enabled(locationId: string) {
  const query = useGeneralConfig('use_solv_pay_v2', locationId, true, false);
  const { pay66EnablePreVisitNewPaymentMethods } = useLDFlags();

  return {
    isSolvPayV2Enabled: query.data && pay66EnablePreVisitNewPaymentMethods,
    isSolvPayV2StatusLoading: query.isLoading,
  };
}
